import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Lightbulb, Palette, Type, Calendar, Image } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import Navbar from '../../components/layout/Navbar';

const tips = [
  {
    title: 'Visual Design Best Practices',
    icon: Palette,
    description: 'Create visually stunning content that captures attention',
    content: [
      'Use the rule of thirds to create balanced, engaging compositions',
      'Maintain consistent brand colors and fonts across all content',
      'Ensure high contrast between text and background for readability',
      'Keep designs clean and uncluttered - less is often more',
      'Use white space effectively to guide the viewer\'s eye',
      'Choose images with good lighting and sharp focus',
      'Consider mobile viewing - designs should work on small screens'
    ]
  },
  {
    title: 'Writing Compelling Captions',
    icon: Type,
    description: 'Craft captions that engage and convert your audience',
    content: [
      'Start with a hook - ask a question or make a bold statement',
      'Keep the first line engaging as it appears in previews',
      'Use storytelling to create emotional connections',
      'Include clear calls-to-action that guide user behavior',
      'Add relevant hashtags but don\'t overdo it (5-10 is optimal)',
      'Write in your brand voice - be authentic and consistent',
      'Use emojis strategically to add personality and break up text'
    ]
  },
  {
    title: 'Choosing the Right Content Types',
    icon: Image,
    description: 'Select content formats that maximize engagement',
    content: [
      'Use carousel posts to tell stories or showcase multiple products',
      'Create video content for higher engagement rates',
      'Share behind-the-scenes content to build authenticity',
      'Post user-generated content to build community',
      'Use infographics to share valuable information quickly',
      'Create polls and interactive content to boost engagement',
      'Mix promotional and educational content (80/20 rule)'
    ]
  },
  {
    title: 'Seasonal Content Planning',
    icon: Calendar,
    description: 'Plan content around seasons, holidays, and trends',
    content: [
      'Create a content calendar with major holidays and events',
      'Plan seasonal campaigns 2-3 months in advance',
      'Research trending topics and hashtags for each season',
      'Adapt your color schemes and imagery to match seasons',
      'Create evergreen content that works year-round',
      'Monitor social media trends and jump on relevant ones quickly',
      'Prepare backup content for unexpected events or changes'
    ]
  }
];

const ContentCreationTips: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50/30">
      <Navbar />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/resources" className="inline-flex items-center text-orange-600 hover:text-orange-700 mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resources
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Content Creation Tips
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              Master the art of creating engaging social media content with these proven strategies and creative techniques.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {tips.map((tip, index) => {
            const Icon = tip.icon;
            return (
              <motion.div
                key={tip.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="bg-white/70 backdrop-blur-sm border border-gray-200/60 shadow-lg">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-gray-900">
                          {tip.title}
                        </CardTitle>
                        <CardDescription className="text-gray-600">
                          {tip.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {tip.content.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-3">
                          <Lightbulb className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Pro Tips */}
        <motion.div
          className="mt-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Card className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0">
            <CardHeader>
              <CardTitle className="text-2xl font-bold flex items-center">
                <Lightbulb className="w-6 h-6 mr-3" />
                Pro Tips for Maximum Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-yellow-100">
                <li>• Test different content types to see what resonates with your audience</li>
                <li>• Analyze your top-performing posts and create similar content</li>
                <li>• Engage with your audience by responding to comments quickly</li>
                <li>• Use analytics to optimize your posting times and frequency</li>
                <li>• Stay authentic - audiences can spot inauthentic content easily</li>
              </ul>
            </CardContent>
          </Card>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200/60 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Create Amazing Content?</h2>
            <p className="text-gray-600 mb-6">
              Put these tips into practice with our AI-powered content creation tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/app/content-creation/ad-studio">
                <Button size="lg" className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white">
                  Start Creating
                </Button>
              </Link>
              <Link to="/resources">
                <Button size="lg" variant="outline">
                  More Resources
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ContentCreationTips;